import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { parse } from 'query-string';
import { encryptTransform } from 'redux-persist-transform-encrypt';

import { AuthorizationResponse } from '@/types/openid';
import { Current } from '@/types/current';
import { persistor, RootState, useAppDispatch } from '@/store';
import { pages } from '@/settings/pages';
import { fetchTokenResponse } from '@/components/pages/Auth/Openid/shared/fetchTokenResponse';
import { OpenIdError } from '@/components/pages/ErrorPage/OpenIdError';
import {
  verifyIdToken,
  decodeIdToken,
} from '@/components/pages/Auth/Openid/shared/verifyIdToken';
import { authStore } from '@/store/auth';
import { affiliationsApi } from '@/services/api';
import i18n from '@/settings/i18n';
import { tokenProcessing } from '@/services/api/base/utils';
import { CenteredLoader } from '@/components/utils/Loader';

import { reLogin } from '../../shared/utils';
import { FROM_PATH_KEY } from '@/constants';
import { isProduction, isStaging } from '@/settings/environment';
import { sentryReportError } from '@/utils/sentryReportError';

const selector = ({ openIdVerification, auth }: RootState) => ({
  state: openIdVerification.state,
  nonce: openIdVerification.nonce,
  codeVerifier: openIdVerification.codeVerifier,
  token: auth.session?.token,
});

export const Container: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [openIdError, setOpenIdError] = useState(false);
  const [returnTo, setReturnTo] = useState<string>();
  const [gloplaId, setGloplaId] = useState<string>();
  const isTokenFetchingRef = useRef(false);
  const { code, state } = parse(location.search, {
    parseNumbers: true,
  }) as AuthorizationResponse;

  const endInFailure = () => {
    isTokenFetchingRef.current = false;
    setOpenIdError(true);
    setLoading(false);
  };

  const {
    state: storedState,
    nonce: storedNonce,
    codeVerifier: storedCodeVerifier,
    token: storedToken,
  } = useSelector(selector);

  const retrieveReturnTo = (encodedNonce: string): string | undefined => {
    try {
      const nonceJson = JSON.parse(atob(encodedNonce));
      const isObject = typeof nonceJson === 'object';
      const isExpired = new Date(nonceJson.expiresAt) <= new Date();

      if (isObject && !isExpired) {
        const fromPath = sessionStorage.getItem(FROM_PATH_KEY);
        if (fromPath) {
          return fromPath as string;
        } else {
          const { pathname, search } = new URL(nonceJson.returnTo);
          return pathname + search;
        }
      } else {
        throw new Error();
      }
    } catch {
      return undefined;
    }
  };

  const handleErrors = useCallback(
    (error: any) => {
      sentryReportError(error, (scope) => {
        scope.setExtras({
          code,
          state,
          storedState,
          storedNonce,
          now: new Date(),
        });
      });
    },
    [code, state, storedNonce, storedState],
  );

  /**
   * 指定した組織IDがlocalStorageに保存されるまで待機する
   * 暗号化されたデータを復号化し、組織IDの一致を確認
   * 10秒のタイムアウトで失敗とみなす
   */
  const waitForStorage = async (organizationId: number) => {
    const STORAGE_TIMEOUT = 10000; // 10秒
    const POLLING_INTERVAL = 100; // 100ミリ秒
    const startTime = Date.now();
    const transform = encryptTransform({
      secretKey: process.env.REACT_APP_PERSIST_KEY || '',
    });

    while (Date.now() - startTime < STORAGE_TIMEOUT) {
      const stored = localStorage.getItem('persist:auth');

      if (stored) {
        try {
          const parsedStored = JSON.parse(stored);
          const currentData = JSON.parse(parsedStored.current);
          // 暗号化データを復号化してJSON文字列を取得
          const decryptedCurrentData = transform.out(
            currentData,
            'auth',
            'auth',
          ) as Current;

          if (decryptedCurrentData.organization?.id === organizationId) {
            return true;
          }
        } catch (error) {
          handleErrors(error);
          return false;
        }
      }

      await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL));
    }

    return false;
  };

  useEffect(() => {
    // React StrictModeでの二重実行を防ぐためのフラグチェック
    if (isTokenFetchingRef.current) {
      return;
    }

    /**
     * MEMO: 選択した組織情報を取得(affiliationsApi)する前に、現在Reduxに格納されているsession、state、nonce及びcodeVerifierを一旦、リセットする
     * 理由:  Auth側にて現在のログイン状態を確認する際、既にrevokeされたアクセストークンを取得する場合が存在する(ログイン後の所属組織の選択)ため、
     *       401の認証エラーが発生する
     */
    persistor.purge();
    const validForState = state && storedState === state;

    if (!(code && validForState && storedNonce && storedCodeVerifier)) {
      // 認証エラー画面表示時に「トップへ」ボタンを押下すれば再認証が可能なため、Sentryのエラー通知は行わない.
      // ※ ただし、後続のトークンリクエストまたはidTokenの取得に失敗した場合においては、Sentryにエラー通知を行う.
      return endInFailure();
    }

    // トークン取得処理を開始したことを記録
    isTokenFetchingRef.current = true;

    (async () => {
      try {
        const tokenResponse = await fetchTokenResponse(
          code,
          storedCodeVerifier,
        );
        const idToken = tokenResponse?.idToken;
        if (!idToken) {
          handleErrors(new Error('idToken is not found in tokenResponse'));
          return endInFailure();
        }
        const payload = decodeIdToken(idToken).payload;

        // MEMO: decodeで参照するglopla idはsnake_caseなので注意、camelCaseではない
        if (payload.glopla_id) {
          // MEMO: IDトークンからlocaleを取得し、言語切替を行う
          i18n.changeLanguage(payload.locale);
          setGloplaId(payload.glopla_id);
        } else {
          const queryParams = {
            error_message_key: 'no_organization_affiliation',
          };
          reLogin(queryParams);
          return;
        }
        setReturnTo(retrieveReturnTo(storedNonce));
        sessionStorage.removeItem(FROM_PATH_KEY);

        try {
          await verifyIdToken(idToken as string, storedNonce);
          dispatch(
            authStore.actions.setSession(tokenProcessing(tokenResponse)),
          );
        } catch (e) {
          handleErrors(e);
          return endInFailure();
        }
      } catch (err) {
        handleErrors(err);
        return endInFailure();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (storedToken && gloplaId) {
        try {
          const myOrganizations = await affiliationsApi.myOrganizations();
          const organization = myOrganizations.find(
            (org) => org.organization.gloplaId === gloplaId,
          );

          if (!organization) {
            const queryParams = {
              error_message_key: 'no_organization_affiliation',
            };
            reLogin(queryParams);
            return;
          }

          dispatch(authStore.actions.selectOrganization(organization));

          // 本番/ステージング環境では、ローカルストレージに組織情報が格納されるかをチェックする
          // 保存に失敗した場合は認証エラーとして再ログインを要求
          if (isProduction() || isStaging()) {
            const isStored = await waitForStorage(organization.organization.id);
            if (!isStored) {
              const queryParams = {
                error_message_key: 'no_organization_affiliation',
              };
              reLogin(queryParams);
              return;
            }
          }
          setLoading(false);

          // 保存確認後に画面遷移
          if (returnTo) {
            window.location.assign(returnTo);
          } else {
            window.location.assign(pages.top);
          }
          return;
        } catch (e) {
          handleErrors(e as Error);
          endInFailure();
          return;
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedToken, gloplaId, dispatch, returnTo, handleErrors]);

  return (
    <>
      {loading && <CenteredLoader />}
      {openIdError && <OpenIdError />}
    </>
  );
};
